import { PossessionBaseEnum, RCAData } from '@insurance/api/requests'
import { InputDatePicker } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { Text } from '@shared/components'
import { DEFAULT_DATE_FORMAT } from '@shared/utils'
import { Col, Divider, Form, Input, Row, Select, Spin } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import { useRcaInsuranceValues } from './RcaInsuranceContext'
import { formProps, operatingModeLabels } from './utils'
import { CreditFields } from '../CreditFields'
import { CustomerFields } from '../PersonsFields'
import { UnderwritingWarningsAlert } from '../UnderwritingWarningsAlert'

export const InformationStep = () => {
  const { t } = useTranslation()
  const { required } = useValidationsField()

  const {
    validFromProps,
    vehicleDataProps,
    priceKey,
    operatingModeProps,
    possessionBaseProps,
    possessionBaseDocNumberProps,
    possessionBaseDocDateProps,
    possessionBaseDocNoteProps,
    isPropertyKey,
    warningsKey
  } = formProps(t)

  const underwritingWarnings = Form.useWatch(warningsKey.name, {
    preserve: true
  })

  const operatingModeOptions = Object.entries(operatingModeLabels).map(
    ([value, label]) => ({
      label: t(label),
      value: Number(value)
    })
  )

  const possessionBaseOptions = Object.values(PossessionBaseEnum).map(
    (value) => ({ label: t(value), value })
  )

  const possessionBase = Form.useWatch<PossessionBaseEnum>(
    possessionBaseProps.name,
    { preserve: true }
  )

  const isProperty = Form.useWatch<boolean>(isPropertyKey.name, {
    preserve: true
  })

  const showPossessionBaseDocFields =
    !!possessionBase && possessionBase !== PossessionBaseEnum.PROPERTY

  const { isCalculatingLoading, handleRecalculate } = useRcaInsuranceValues()

  const price = Form.useWatch(priceKey.name, { preserve: true })

  const vehicleData = Form.useWatch<RCAData>(vehicleDataProps.name, {
    preserve: true
  })

  const contractorName = vehicleData
    ? [vehicleData.first_name, vehicleData.last_name].filter(Boolean).join(' ')
    : undefined

  const vehicleInfo = vehicleData
    ? [vehicleData.brand, vehicleData.model, vehicleData.registration_number]
        .filter(Boolean)
        .join(' ')
    : undefined

  const trailerInfo = vehicleData?._trailer
    ? [
        vehicleData._trailer.brand,
        vehicleData._trailer.model,
        vehicleData._trailer.registration_number
      ]
        .filter(Boolean)
        .join(' ')
    : undefined

  const minValidFrom = vehicleData?.minimal_start
    ? dayjs(vehicleData.minimal_start)
    : dayjs()

  const maxValidTo = dayjs().add(365, 'days')

  return (
    <Spin spinning={isCalculatingLoading}>
      <UnderwritingWarningsAlert warnings={underwritingWarnings} />

      {vehicleData && (
        <>
          <Row gutter={[12, 12]}>
            {contractorName && (
              <Col xs={24} lg={12}>
                <Form.Item label={t('contractor')} className="m-0">
                  <Text strong color="colorPrimary">
                    {contractorName}
                  </Text>
                </Form.Item>
              </Col>
            )}

            {vehicleInfo && (
              <Col xs={24} lg={12}>
                <Form.Item
                  label={
                    trailerInfo
                      ? t('towing_vehicle_brand_and_model')
                      : t('vehicle_brand_and_model')
                  }
                  className="m-0"
                >
                  <Text strong color="colorPrimary">
                    {vehicleInfo}
                  </Text>
                </Form.Item>
              </Col>
            )}

            {trailerInfo && (
              <Col xs={24} lg={12}>
                <Form.Item label={t('trailer_brand_and_model')} className="m-0">
                  <Text strong color="colorPrimary">
                    {trailerInfo}
                  </Text>
                </Form.Item>
              </Col>
            )}

            {vehicleData.bonus_malus !== undefined && (
              <Col xs={24} lg={12}>
                <Form.Item label={t('bonus_malus')} className="m-0">
                  <Text strong color="colorPrimary">
                    {vehicleData.bonus_malus}
                  </Text>
                </Form.Item>
              </Col>
            )}

            {vehicleData.expire && (
              <Col xs={24} lg={12}>
                <Form.Item label={t('current_policy_expire')} className="m-0">
                  <Text strong color="colorPrimary">
                    {dayjs(vehicleData.expire).format(DEFAULT_DATE_FORMAT)}
                  </Text>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Divider />
        </>
      )}

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <Form.Item {...validFromProps} rules={[required]}>
            <InputDatePicker
              placeholder={validFromProps.label}
              minDate={minValidFrom}
              maxDate={maxValidTo}
            />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item {...operatingModeProps}>
            <Select
              placeholder={operatingModeProps.label}
              options={operatingModeOptions}
              allowClear
              onChange={handleRecalculate}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            {...possessionBaseProps}
            rules={isProperty ? [] : [required]}
          >
            <Select
              placeholder={possessionBaseProps.label}
              options={possessionBaseOptions}
              allowClear
              onChange={handleRecalculate}
            />
          </Form.Item>
        </Col>

        {showPossessionBaseDocFields && (
          <>
            <Col xs={24} lg={12}>
              <Form.Item {...possessionBaseDocNumberProps}>
                <Input
                  placeholder={possessionBaseDocNumberProps.label}
                  maxLength={100}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item {...possessionBaseDocDateProps}>
                <InputDatePicker
                  placeholder={possessionBaseDocDateProps.label}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item {...possessionBaseDocNoteProps}>
                <Input.TextArea
                  placeholder={possessionBaseDocNoteProps.label}
                  maxLength={300}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      <Divider />

      <CustomerFields isEditable />
      <CreditFields orderPrice={price?.amount} />
    </Spin>
  )
}
